import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { removeAuthToken } from '../../utils/auth';
import { useTotalIncompleteCount, useTotalUnviewedCount } from '../../hooks/useChildrenCounts';
import NotificationBadge from '../common/NotificationBadge';

export default function MobileMenu() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const NAV_ITEMS = [
    { path: '/', label: t('nav.courses'), exact: true },
    { path: '/research', label: t('nav.research'), exact: false },
    { path: '/consulting', label: t('nav.consulting'), exact: false },
    { path: '/planner', label: t('nav.planner'), exact: false },
  ];
  const { totalCount: incompleteCount } = useTotalIncompleteCount();
  const { totalCount: unviewedCount } = useTotalUnviewedCount();
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const getActiveIndex = () => {
    return NAV_ITEMS.findIndex(item => isActive(item.path, item.exact));
  };

  // Update indicator position
  const updateIndicator = () => {
    const activeIndex = getActiveIndex();
    if (activeIndex === -1 || !containerRef.current) {
      setIndicatorStyle({ left: 0, width: 0, opacity: 0 });
      return;
    }

    const activeEl = itemRefs.current[activeIndex];
    if (!activeEl) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();

    setIndicatorStyle({
      left: activeRect.left - containerRect.left,
      width: activeRect.width,
      opacity: 1,
    });
  };

  // Update indicator on route change, initialization, and language change
  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      updateIndicator();
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }, isInitialized ? 10 : 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateIndicator is stable, re-run on route/init/language change
  }, [location.pathname, isInitialized, i18n.language]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => updateIndicator();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateIndicator is stable, only set up listener once
  }, []);

  const handleLogout = () => {
    removeAuthToken();
    window.location.href = '/login';
  };

  const activeIndex = getActiveIndex();

  return (
    <>
      {/* Desktop menu */}
      <div ref={containerRef} className="hidden sm:flex items-center gap-0.5 md:gap-1 relative">
        {/* Animated underbar indicator */}
        <div
          className="absolute bottom-0 h-0.5 bg-[#5E9ED6]"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
            opacity: indicatorStyle.opacity ?? 1,
            transition: 'all 700ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />

        {NAV_ITEMS.map((item, index) => (
          <Link
            key={item.path}
            ref={el => itemRefs.current[index] = el}
            to={item.path}
            className={`relative px-2 md:px-3 py-2 text-xs md:text-sm font-medium whitespace-nowrap transition-colors duration-150 ${
              activeIndex === index
                ? 'text-[#5E9ED6]'
                : 'text-gray-600 dark:text-gray-300 hover:text-[#5E9ED6]'
            }`}
          >
            {item.label}
            {item.path === '/research' && (
              <NotificationBadge count={incompleteCount} className="absolute -top-1 -right-1" />
            )}
            {item.path === '/planner' && (
              <NotificationBadge count={unviewedCount} className="absolute -top-1 -right-1" />
            )}
          </Link>
        ))}

        <button
          onClick={handleLogout}
          className="px-2 md:px-3 py-2 text-xs md:text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-[#5E9ED6] whitespace-nowrap transition-colors duration-150"
        >
          {t('nav.logout')}
        </button>
      </div>
    </>
  );
}
